import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import { api } from '../../services/api';
import { incidentsApi, type Incident } from '../../services/incidentsApi';
import type { Zone } from '../../services/zonesApi';
import {
  incidentUsesReviewerAidPrimaryPolicy,
} from '../../domain/incidents/reviewerImagePolicy';
import {
  selectPreferredReviewAttachment,
  type PreferredReviewAttachment,
} from '../../domain/incidents/preferredReviewMedia';
import { zoneOverlayPolygon } from '../../domain/liveView/zoneOverlayGeometry';
import { presentAreaLabel } from '../../utils/presentAreaLabel';

type PreferredAttachment = PreferredReviewAttachment;

export type IncidentEvidenceVideoIdentity = {
  clipId: string;
  filename: string;
  objectUrl: string;
};

export type IncidentEvidenceMoment = {
  clipId: string;
  offsetMs: number;
};

type UseIncidentEvidenceArgs = {
  allZones: Zone[];
  selectedIncident: Incident | null;
  viewDialogOpen: boolean;
  findingForIncident: (
    incident: Incident | null | undefined,
  ) => { kind?: 'none' | 'chip' | 'text'; label?: string } | null | undefined;
  incidentTypeLabel: (raw: any) => string;
};

const observationEpisodeIdOf = (incident: Incident | null | undefined): string =>
  String((incident as Incident & { observation_episode_id?: string | null })?.observation_episode_id || '').trim();

const attachmentClientEvidenceId = (attachment: any): string => String(
  attachment?.client_evidence_id
  ?? attachment?.metadata?.client_evidence_id
  ?? attachment?.metadata?.edge_replay?.client_evidence_id
  ?? attachment?.metadata?.extra?.client_evidence_id
  ?? attachment?.metadata?.extra?.edge_replay?.client_evidence_id
  ?? '',
).trim();

const attachmentEdgeClipId = (attachment: any): string => String(
  attachment?.edge_clip_id
  ?? attachment?.metadata?.edge_clip_id
  ?? attachment?.metadata?.extra?.edge_clip_id
  ?? attachment?.metadata?.edge_replay?.edge_clip_id
  ?? '',
).trim();

const edgeClipIdentity = (
  clipId: string,
): { incidentId: string; filename: string } | null => {
  const token = String(clipId || '').trim();
  if (!token.startsWith('edge-clip:')) return null;
  const parts = token.split(':');
  // edge-clip:<incident_id>:<filename...>
  const incidentId = String(parts[1] || '').trim();
  const filename = parts.length >= 3 ? parts.slice(2).join(':').trim() : '';
  return incidentId && filename ? { incidentId, filename } : null;
};

const edgeClipFilename = (clipId: string): string =>
  edgeClipIdentity(clipId)?.filename || '';

const attachmentLooksLikeVideo = (attachment: any): boolean => {
  const filename = String(attachment?.filename || '').trim().toLowerCase();
  const type = String(attachment?.type || attachment?.kind || '').trim().toLowerCase();
  const mime = String(attachment?.mimetype || attachment?.mime_type || '').trim().toLowerCase();
  if (filename.endsWith('.mp4') || filename.endsWith('.webm') || filename.endsWith('.mov')) {
    return true;
  }
  if (type.includes('video') || type.includes('clip')) return true;
  if (mime.startsWith('video/')) return true;
  return false;
};

const attachmentMatchesClipId = (attachment: any, clipId: string): boolean => {
  const wanted = String(clipId || '').trim();
  if (!wanted || !attachmentLooksLikeVideo(attachment)) return false;
  if (attachmentClientEvidenceId(attachment) === wanted) return true;
  if (attachmentEdgeClipId(attachment) === wanted) return true;
  const filenameFromClip = edgeClipFilename(wanted);
  if (!filenameFromClip) return false;
  const attachmentFilename = String(attachment?.filename || '').trim();
  if (attachmentFilename === filenameFromClip) return true;
  const edgeFilename = String(
    attachment?.edge_clip_filename
    ?? attachment?.metadata?.edge_clip_filename
    ?? attachment?.metadata?.extra?.edge_clip_filename
    ?? '',
  ).trim();
  return edgeFilename === filenameFromClip;
};

export function useIncidentEvidence({
  allZones,
  selectedIncident,
  viewDialogOpen,
  findingForIncident,
  incidentTypeLabel,
}: UseIncidentEvidenceArgs) {
  const [latestSnapshotObjectUrl, setLatestSnapshotObjectUrl] = useState<string | null>(null);
  const [latestSnapshotLoading, setLatestSnapshotLoading] = useState(false);
  const [latestSnapshotError, setLatestSnapshotError] = useState<string | null>(null);
  const [latestVideoObjectUrl, setLatestVideoObjectUrl] = useState<string | null>(null);
  const [latestVideoLoading, setLatestVideoLoading] = useState(false);
  const [latestVideoError, setLatestVideoError] = useState<string | null>(null);
  const [latestVideoIdentity, setLatestVideoIdentity] = useState<IncidentEvidenceVideoIdentity | null>(null);
  const [evidencePopover, setEvidencePopover] = useState<{
    open: boolean;
    anchorEl: HTMLElement | null;
    incident: Incident | null;
  }>({ open: false, anchorEl: null, incident: null });
  const [evidencePreviewUrlByIncident, setEvidencePreviewUrlByIncident] = useState<Record<string, string>>({});
  const [evidencePreviewLoadingByIncident, setEvidencePreviewLoadingByIncident] = useState<Record<string, boolean>>({});
  const [evidencePreviewErrorByIncident, setEvidencePreviewErrorByIncident] = useState<Record<string, string>>({});

  const latestSnapshotUrlRef = useRef<string | null>(null);
  const latestVideoUrlRef = useRef<string | null>(null);
  const videoRequestIdRef = useRef(0);
  const previewUrlsRef = useRef<Record<string, string>>({});
  const clipPromoteRequestedRef = useRef<Set<string>>(new Set());
  /** When set, auto latest-video reload must not replace a View-moment clip. */
  const pinnedEvidenceMomentClipIdRef = useRef<string | null>(null);
  const pinnedEvidenceMomentIncidentIdRef = useRef<string | null>(null);

  const revokeLatestSnapshotUrl = useCallback(() => {
    const currentUrl = latestSnapshotUrlRef.current;
    if (!currentUrl) return;
    try {
      URL.revokeObjectURL(currentUrl);
    } catch {
      // ignore
    }
    latestSnapshotUrlRef.current = null;
  }, []);

  const clearLatestSnapshotUrl = useCallback(() => {
    revokeLatestSnapshotUrl();
    setLatestSnapshotObjectUrl(null);
  }, [revokeLatestSnapshotUrl]);

  const revokeLatestVideoUrl = useCallback(() => {
    const currentUrl = latestVideoUrlRef.current;
    if (!currentUrl) return;
    try {
      URL.revokeObjectURL(currentUrl);
    } catch {
      // ignore
    }
    latestVideoUrlRef.current = null;
  }, []);

  const clearLatestVideoUrl = useCallback(() => {
    revokeLatestVideoUrl();
    setLatestVideoObjectUrl(null);
    setLatestVideoIdentity(null);
  }, [revokeLatestVideoUrl]);

  useEffect(() => {
    previewUrlsRef.current = evidencePreviewUrlByIncident;
  }, [evidencePreviewUrlByIncident]);

  useEffect(() => {
    return () => {
      revokeLatestSnapshotUrl();
      revokeLatestVideoUrl();
      for (const url of Object.values(previewUrlsRef.current || {})) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore
        }
      }
    };
  }, [revokeLatestSnapshotUrl, revokeLatestVideoUrl]);

  function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1500);
  }

  const fetchLatestSnapshotBlob = useCallback(async (incidentId: string): Promise<Blob> => {
    const res = await api.get(`/api/v1/incidents/${encodeURIComponent(incidentId)}/evidence/latest/snapshot`, {
      responseType: 'blob',
    });
    return res.data as Blob;
  }, []);

  const fetchLatestVideoBlob = useCallback(async (incidentId: string): Promise<Blob> => {
    const res = await api.get(`/api/v1/incidents/${encodeURIComponent(incidentId)}/evidence/latest/video`, {
      responseType: 'blob',
    });
    return res.data as Blob;
  }, []);

  const fetchEvidenceFileBlob = useCallback(async (incidentId: string, filename: string): Promise<Blob> => {
    const res = await api.get(
      `/api/v1/incidents/${encodeURIComponent(incidentId)}/evidence/file/${encodeURIComponent(filename)}`,
      { responseType: 'blob' },
    );
    return res.data as Blob;
  }, []);

  const loadEvidenceMoment = useCallback(
    async (moment: IncidentEvidenceMoment): Promise<IncidentEvidenceVideoIdentity> => {
      const incidentId = String(selectedIncident?.incident_id || '').trim();
      const clipId = String(moment?.clipId || '').trim();
      if (!incidentId || !clipId) {
        const message = 'This evidence moment does not identify a loadable clip.';
        setLatestVideoError(message);
        throw new Error(message);
      }

      // Pin before async work so incident polling cannot replace this moment clip.
      pinnedEvidenceMomentClipIdRef.current = clipId;
      pinnedEvidenceMomentIncidentIdRef.current = incidentId;

      if (
        latestVideoIdentity?.clipId === clipId
        && latestVideoIdentity.objectUrl === latestVideoUrlRef.current
      ) {
        setLatestVideoError(null);
        return latestVideoIdentity;
      }

      const incidentAttachments = selectedIncident?.evidence?.attachments;
      const attachments = Array.isArray(incidentAttachments) ? incidentAttachments : [];
      const attachment = attachments.find(
        (candidate: any) => attachmentMatchesClipId(candidate, clipId),
      ) as any;
      const clipIdentity = edgeClipIdentity(clipId);
      const filename = String(
        attachment?.filename || clipIdentity?.filename || '',
      ).trim();
      const evidenceIncidentId = String(
        attachment ? incidentId : clipIdentity?.incidentId || '',
      ).trim();
      if (!filename || !evidenceIncidentId) {
        pinnedEvidenceMomentClipIdRef.current = null;
        pinnedEvidenceMomentIncidentIdRef.current = null;
        const message = `The clip for this evidence moment (${clipId}) is not attached to this incident.`;
        setLatestVideoError(message);
        throw new Error(message);
      }

      const requestId = ++videoRequestIdRef.current;
      setLatestVideoLoading(true);
      setLatestVideoError(null);
      try {
        const blob = await fetchEvidenceFileBlob(evidenceIncidentId, filename);
        if (requestId !== videoRequestIdRef.current) {
          throw new Error('A newer evidence clip request replaced this one.');
        }
        const objectUrl = URL.createObjectURL(blob);
        const priorUrl = latestVideoUrlRef.current;
        latestVideoUrlRef.current = objectUrl;
        setLatestVideoObjectUrl(objectUrl);
        const identity = { clipId, filename, objectUrl };
        setLatestVideoIdentity(identity);
        if (priorUrl && priorUrl !== objectUrl) {
          try {
            URL.revokeObjectURL(priorUrl);
          } catch {
            // ignore
          }
        }
        return identity;
      } catch (error: any) {
        if (
          pinnedEvidenceMomentClipIdRef.current === clipId
          && pinnedEvidenceMomentIncidentIdRef.current === incidentId
        ) {
          pinnedEvidenceMomentClipIdRef.current = null;
          pinnedEvidenceMomentIncidentIdRef.current = null;
        }
        const message = error?.response?.data?.error || error?.message || 'Failed to load evidence moment clip';
        if (requestId === videoRequestIdRef.current) {
          setLatestVideoError(String(message));
        }
        throw error;
      } finally {
        if (requestId === videoRequestIdRef.current) {
          setLatestVideoLoading(false);
        }
      }
    },
    [fetchEvidenceFileBlob, latestVideoIdentity, selectedIncident],
  );

  const bestAttachmentByType = useCallback(
    (incident: Incident | null | undefined, kind: 'snapshot' | 'video'): PreferredAttachment | null => (
      selectPreferredReviewAttachment(incident, kind)
    ),
    [],
  );

  const bestSnapshotAttachment = useCallback(
    (incident: Incident | null | undefined): PreferredAttachment | null => bestAttachmentByType(incident, 'snapshot'),
    [bestAttachmentByType],
  );

  const bestVideoAttachment = useCallback(
    (incident: Incident | null | undefined): PreferredAttachment | null => bestAttachmentByType(incident, 'video'),
    [bestAttachmentByType],
  );

  const humanLabelCompact = useCallback(
    (incident: Incident | null | undefined): string => {
      if (!incident) return '';
      const type = incidentTypeLabel(String(incident.incident_type || incident.title || ''));
      let finding = '';
      try {
        const value = findingForIncident(incident as any);
        if (value?.label) finding = String(value.label);
      } catch {
        finding = '';
      }
      const zone = presentAreaLabel(String(incident.zone_id || '').trim());
      const camera = String(incident.camera_id || '').trim();
      return [type, finding, zone, camera].filter(Boolean).join(' – ');
    },
    [findingForIncident, incidentTypeLabel],
  );

  const hydrateIncidentEvidence = useCallback(
    async (incident: Incident): Promise<Incident> => {
      const incidentId = String(incident?.incident_id || '').trim();
      if (!incidentId) return incident;
      if (observationEpisodeIdOf(incident)) return incident;
      const evidence = incident.evidence || {};
      const attachments = Array.isArray(evidence.attachments) ? evidence.attachments : [];
      const hasLatestSnapshot = Boolean((evidence as any)?.latest?.snapshot?.filename);
      const hasLatestVideo = Boolean((evidence as any)?.latest?.video?.filename);
      if (attachments.length > 0 || hasLatestSnapshot || hasLatestVideo) {
        return incident;
      }
      try {
        return await incidentsApi.getIncident(incidentId);
      } catch {
        return incident;
      }
    },
    [],
  );

  const overlaySnapshotBlob = useCallback(
    async (blob: Blob, opts: { label: string; ts: string; zone_id?: string; camera_id?: string }): Promise<Blob> => {
      const url = URL.createObjectURL(blob);
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('image_load_failed'));
          img.src = url;
        });
        const width = img.naturalWidth || img.width;
        const height = img.naturalHeight || img.height;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return blob;

        ctx.drawImage(img, 0, 0, width, height);

        let polygon: number[][] | null = null;
        try {
          const zoneId = String(opts.zone_id || '').trim();
          const cameraId = String(opts.camera_id || '').trim();
          const zone =
            (allZones || []).find(
              (value: any) =>
                String(value?.zone_id || '').trim() === zoneId &&
                (!cameraId || !value?.camera_id || String(value.camera_id).trim() === cameraId),
            ) ||
            (allZones || []).find((value: any) => String(value?.name || '').trim() === zoneId) ||
            (allZones || []).find((value: any) => String(value?.zone_id || '').trim() === zoneId);
          if (zone) {
            polygon = zoneOverlayPolygon(zone as any);
          }
        } catch {
          polygon = null;
        }

        ctx.save();
        ctx.lineWidth = Math.max(3, Math.round(Math.min(width, height) * 0.006));
        ctx.strokeStyle = 'rgba(0, 200, 83, 0.95)';
        ctx.fillStyle = 'rgba(0, 200, 83, 0.18)';
        if (polygon && polygon.length > 2) {
          ctx.beginPath();
          for (let i = 0; i < polygon.length; i += 1) {
            const point = polygon[i];
            const x = Math.max(0, Math.min(1, Number(point?.[0] ?? 0))) * width;
            const y = Math.max(0, Math.min(1, Number(point?.[1] ?? 0))) * height;
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
        }
        ctx.restore();

        const label = String(opts.label || '').trim();
        const ts = String(opts.ts || '').trim();
        const stamp = ts ? new Date(ts).toLocaleString() : '';
        const text = [label, stamp].filter(Boolean).join('  •  ');
        if (text) {
          const pad = Math.max(10, Math.round(Math.min(width, height) * 0.012));
          const fontSize = Math.max(18, Math.round(Math.min(width, height) * 0.028));
          ctx.save();
          ctx.font = `700 ${fontSize}px system-ui, -apple-system, Segoe UI, Roboto, Arial`;
          const metrics = ctx.measureText(text);
          const boxWidth = Math.min(width - 2 * pad, metrics.width + 2 * pad);
          const boxHeight = Math.round(fontSize * 1.45);
          ctx.fillStyle = 'rgba(0,0,0,0.72)';
          ctx.fillRect(pad, pad, boxWidth, boxHeight);
          ctx.fillStyle = 'rgba(255,255,255,0.98)';
          ctx.textBaseline = 'middle';
          ctx.fillText(text, pad + Math.round(pad * 0.7), pad + Math.round(boxHeight / 2));
          ctx.restore();
        }

        const output = await new Promise<Blob | null>((resolve) => canvas.toBlob((value) => resolve(value), 'image/jpeg', 0.92));
        return output || blob;
      } catch {
        return blob;
      } finally {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore
        }
      }
    },
    [allZones],
  );

  const openEvidencePopover = useCallback(
    async (event: MouseEvent<HTMLElement>, row: Incident) => {
      event.stopPropagation();
      const incident = await hydrateIncidentEvidence(row);
      setEvidencePopover({ open: true, anchorEl: event.currentTarget, incident });

      const incidentId = incident?.incident_id;
      const pick = bestSnapshotAttachment(incident);
      const episodeScoped = Boolean(observationEpisodeIdOf(incident));
      if (!incidentId || !pick?.filename) return;
      if (episodeScoped && !pick.preferFileEndpoint) return;
      if (previewUrlsRef.current[incidentId]) return;
      if (evidencePreviewLoadingByIncident[incidentId]) return;

      try {
        setEvidencePreviewLoadingByIncident((prev) => ({ ...prev, [incidentId]: true }));
        setEvidencePreviewErrorByIncident((prev) => ({ ...prev, [incidentId]: '' }));
        if (episodeScoped && !pick.preferFileEndpoint) return;
        const blob = pick.preferFileEndpoint
          ? await fetchEvidenceFileBlob(incidentId, pick.filename)
          : await fetchLatestSnapshotBlob(incidentId);
        const overlayed = incidentUsesReviewerAidPrimaryPolicy(incident) || pick?.preAnnotated
          ? blob
          : await overlaySnapshotBlob(blob, {
              label: humanLabelCompact(incident),
              ts: String(incident?.created_at || ''),
              zone_id: incident?.zone_id,
              camera_id: incident?.camera_id,
            });
        const url = URL.createObjectURL(overlayed);
        setEvidencePreviewUrlByIncident((prev) => {
          const prior = prev[incidentId];
          if (prior && prior !== url) {
            try {
              URL.revokeObjectURL(prior);
            } catch {
              // ignore
            }
          }
          return { ...prev, [incidentId]: url };
        });
      } catch (error: any) {
        setEvidencePreviewErrorByIncident((prev) => ({
          ...prev,
          [incidentId]: error?.response?.data?.error || error?.message || 'Failed to load snapshot',
        }));
      } finally {
        setEvidencePreviewLoadingByIncident((prev) => ({ ...prev, [incidentId]: false }));
      }
    },
    [
      bestSnapshotAttachment,
      evidencePreviewLoadingByIncident,
      fetchEvidenceFileBlob,
      fetchLatestSnapshotBlob,
      hydrateIncidentEvidence,
      humanLabelCompact,
      overlaySnapshotBlob,
    ],
  );

  const closeEvidencePopover = useCallback(() => {
    setEvidencePopover({ open: false, anchorEl: null, incident: null });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      clearLatestSnapshotUrl();
      setLatestSnapshotError(null);

      if (!viewDialogOpen || !selectedIncident?.incident_id) return;
      const pick = bestSnapshotAttachment(selectedIncident);
      const strictReviewerImagePolicy = incidentUsesReviewerAidPrimaryPolicy(selectedIncident);
      const rawSnapshotStatus = String((selectedIncident as any)?.snapshot_status || '').trim().toUpperCase();
      const snapshotUri = String((selectedIncident as any)?.snapshot_uri || '').trim();
      const episodeScoped = Boolean(observationEpisodeIdOf(selectedIncident));
      const shouldLoadSnapshot = episodeScoped
        ? Boolean(pick?.filename && pick.preferFileEndpoint)
        : strictReviewerImagePolicy
          ? Boolean(pick?.filename)
          : Boolean(pick?.filename) || rawSnapshotStatus === 'AVAILABLE' || Boolean(snapshotUri);
      if (!shouldLoadSnapshot) return;

      try {
        setLatestSnapshotLoading(true);
        const blob =
          pick?.filename && pick.preferFileEndpoint
            ? await fetchEvidenceFileBlob(selectedIncident.incident_id, pick.filename)
            : episodeScoped
              ? null
              : await fetchLatestSnapshotBlob(selectedIncident.incident_id);
        if (!blob) return;
        if (cancelled) return;
        const overlayed = strictReviewerImagePolicy || pick?.preAnnotated
          ? blob
          : await overlaySnapshotBlob(blob, {
              label: humanLabelCompact(selectedIncident),
              ts: String(selectedIncident.created_at || ''),
              zone_id: selectedIncident.zone_id,
              camera_id: selectedIncident.camera_id,
            });
        if (cancelled) return;
        const url = URL.createObjectURL(overlayed);
        latestSnapshotUrlRef.current = url;
        setLatestSnapshotObjectUrl(url);
      } catch (error: any) {
        if (cancelled) return;
        setLatestSnapshotError(error?.response?.data?.error || error?.message || 'Failed to load snapshot');
      } finally {
        if (!cancelled) setLatestSnapshotLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [
    bestSnapshotAttachment,
    clearLatestSnapshotUrl,
    fetchEvidenceFileBlob,
    fetchLatestSnapshotBlob,
    humanLabelCompact,
    overlaySnapshotBlob,
    selectedIncident,
    viewDialogOpen,
  ]);

  useEffect(() => {
    let cancelled = false;
    const incidentId = String(selectedIncident?.incident_id || '').trim();
    if (!viewDialogOpen || !incidentId) {
      pinnedEvidenceMomentClipIdRef.current = null;
      pinnedEvidenceMomentIncidentIdRef.current = null;
      clearLatestVideoUrl();
      setLatestVideoError(null);
      return () => {
        cancelled = true;
      };
    }
    if (pinnedEvidenceMomentIncidentIdRef.current && pinnedEvidenceMomentIncidentIdRef.current !== incidentId) {
      pinnedEvidenceMomentClipIdRef.current = null;
      pinnedEvidenceMomentIncidentIdRef.current = null;
    }
    // Incident polling refreshes selectedIncident and would otherwise replace a
    // View-moment clip with the newest (often short) review_ring asset.
    if (
      pinnedEvidenceMomentClipIdRef.current
      && pinnedEvidenceMomentIncidentIdRef.current === incidentId
    ) {
      return () => {
        cancelled = true;
      };
    }

    const requestId = ++videoRequestIdRef.current;

    const run = async () => {
      clearLatestVideoUrl();
      setLatestVideoError(null);

      if (!selectedIncident?.incident_id) return;
      const pick = bestVideoAttachment(selectedIncident);
      const rawClipStatus = String((selectedIncident as any)?.clip_status || '').trim().toUpperCase();
      const clipUri = String((selectedIncident as any)?.clip_uri || '').trim();
      const clipAvailableOnEdge = Boolean((selectedIncident as any)?.clip_available_on_edge);
      const clipPromotionState = String((selectedIncident as any)?.clip_promotion_state || '').trim().toUpperCase();
      const videoAttachment = Array.isArray(selectedIncident.evidence?.attachments)
        ? (selectedIncident.evidence?.attachments as any[]).some((attachment: any) =>
            /\.(mp4|mov|mkv|webm)$/i.test(String(attachment?.filename || '').trim()),
          )
        : false;
      const episodeScoped = Boolean(observationEpisodeIdOf(selectedIncident));
      const shouldLoadVideo = episodeScoped
        ? Boolean(pick?.filename && pick.preferFileEndpoint)
        : rawClipStatus === 'AVAILABLE' || Boolean(clipUri) || Boolean(pick?.filename) || videoAttachment;
      if (!shouldLoadVideo) {
        if (episodeScoped) return;
        if (clipAvailableOnEdge) {
          const incidentId = selectedIncident.incident_id;
          const shouldPromote =
            clipPromotionState !== 'PROMOTING' &&
            clipPromotionState !== 'UPLOADED' &&
            !clipPromoteRequestedRef.current.has(incidentId);
          if (shouldPromote) {
            clipPromoteRequestedRef.current.add(incidentId);
            try {
              await incidentsApi.promoteClip(incidentId);
              if (cancelled) return;
              setLatestVideoError('Clip promotion in progress');
              return;
            } catch (error: any) {
              if (cancelled) return;
              const detail =
                error?.response?.data?.error ||
                error?.response?.data?.promote?.error ||
                error?.message ||
                'Clip promotion failed';
              setLatestVideoError(String(detail));
              return;
            }
          }
          setLatestVideoError(
            clipPromotionState === 'PROMOTING'
              ? 'Clip promotion in progress'
              : 'Clip available on edge',
          );
        }
        return;
      }

      try {
        setLatestVideoLoading(true);
        const blob =
          pick?.filename && pick.preferFileEndpoint
            ? await fetchEvidenceFileBlob(selectedIncident.incident_id, pick.filename)
            : episodeScoped
              ? null
              : await fetchLatestVideoBlob(selectedIncident.incident_id);
        if (!blob) return;
        if (cancelled || requestId !== videoRequestIdRef.current) return;
        const url = URL.createObjectURL(blob);
        latestVideoUrlRef.current = url;
        setLatestVideoObjectUrl(url);
        const filename = String(pick?.filename || '').trim();
        const incidentAttachments = selectedIncident.evidence?.attachments;
        const attachment = Array.isArray(incidentAttachments)
          ? (incidentAttachments as any[]).find(
              (candidate: any) => String(candidate?.filename || '').trim() === filename,
            )
          : null;
        setLatestVideoIdentity({
          clipId: attachmentClientEvidenceId(attachment),
          filename,
          objectUrl: url,
        });
      } catch (error: any) {
        if (cancelled || requestId !== videoRequestIdRef.current) return;
        setLatestVideoError(error?.response?.data?.error || error?.message || 'Failed to load clip');
      } finally {
        if (!cancelled && requestId === videoRequestIdRef.current) setLatestVideoLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [bestVideoAttachment, clearLatestVideoUrl, fetchEvidenceFileBlob, fetchLatestVideoBlob, selectedIncident, viewDialogOpen]);

  return {
    latestSnapshotObjectUrl,
    latestSnapshotLoading,
    latestSnapshotError,
    latestVideoObjectUrl,
    latestVideoLoading,
    latestVideoError,
    latestVideoIdentity,
    loadEvidenceMoment,
    downloadBlob,
    fetchLatestSnapshotBlob,
    fetchLatestVideoBlob,
    fetchEvidenceFileBlob,
    _bestSnapshotAttachment: bestSnapshotAttachment,
    _bestVideoAttachment: bestVideoAttachment,
    _humanLabelCompact: humanLabelCompact,
    _overlaySnapshotBlob: overlaySnapshotBlob,
    evidencePopover,
    evidencePreviewUrlByIncident,
    evidencePreviewLoadingByIncident,
    evidencePreviewErrorByIncident,
    openEvidencePopover,
    closeEvidencePopover,
  };
}
